import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, TrendingUp } from 'lucide-react';
import { GameSummary } from '@/api/types';
import { useTrackClick } from '@/hooks';
import { buildImageUrl } from '@/utils/image-url';
import { calculateHeat } from '@/utils/heat-score';
import styles from './desktop-carousel.module.css';

interface DesktopCarouselProps {
  games: GameSummary[];
  autoplayInterval?: number;
}

export const DesktopCarousel: React.FC<DesktopCarouselProps> = ({
  games,
  autoplayInterval = 5000,
}) => {
  const [current, setCurrent] = useState(0);
  const trackClick = useTrackClick();

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % games.length);
  }, [games.length]);

  const prev = () => {
    setCurrent((prev) => (prev - 1 + games.length) % games.length);
  };

  const goTo = (index: number) => {
    setCurrent(index);
  };

  // 触摸/拖拽滑动支持
  const [dragStart, setDragStart] = useState<number | null>(null);
  const [dragEnd, setDragEnd] = useState<number | null>(null);
  const minSwipeDistance = 50;

  const onDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setDragEnd(null);
    setDragStart(clientX);
  };

  const onDragMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (dragStart === null) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setDragEnd(clientX);
  };

  const onDragEnd = () => {
    if (!dragStart || !dragEnd) return;
    const distance = dragStart - dragEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      next();
    } else if (isRightSwipe) {
      prev();
    }
    setDragStart(null);
    setDragEnd(null);
  };

  useEffect(() => {
    if (games.length <= 1) return;
    const timer = setInterval(next, autoplayInterval);
    return () => clearInterval(timer);
  }, [games.length, autoplayInterval, next]);

  if (games.length === 0) return null;

  const currentGame = games[current];
  const heatScore = calculateHeat(currentGame.viewCount, 0);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // 先记录点击，然后导航
    trackClick.mutate({ 
      gameId: parseInt(currentGame.id, 10) || 0, 
      platformId: 'default' 
    });
    // 使用 window.location 确保正确导航
    window.location.href = `/game/${currentGame.id}`;
  };

  return (
    <div className={styles.carousel}>
      <div className={styles.main}
        onMouseDown={onDragStart}
        onMouseMove={onDragMove}
        onMouseUp={onDragEnd}
        onMouseLeave={onDragEnd}
        onTouchStart={onDragStart}
        onTouchMove={onDragMove}
        onTouchEnd={onDragEnd}
      >
        <a
          href={`/game/${currentGame.id}`}
          className={styles.slide}
          onClick={handleClick}
        >
          <img
            src={buildImageUrl(currentGame.cover)}
            alt={currentGame.title}
            className={styles.image}
          />
          <div className={styles.overlay}>
            <div className={styles.badge}>
              <TrendingUp size={14} />
              热门推荐
            </div>
            <h3 className={styles.title}>{currentGame.title}</h3>
            <p className={styles.category}>{currentGame.category}</p>
            <div className={styles.heat}>
              <TrendingUp size={14} />
              热度 {heatScore}
            </div>
          </div>
        </a>

        <button className={`${styles.arrow} ${styles.prev}`} onClick={prev}>
          <ChevronLeft size={24} />
        </button>
        <button className={`${styles.arrow} ${styles.next}`} onClick={next}>
          <ChevronRight size={24} />
        </button>
      </div>

      <div className={styles.thumbs}>
        {games.map((game, index) => (
          <button
            key={game.id}
            className={`${styles.thumb} ${index === current ? styles.active : ''}`}
            onClick={() => goTo(index)}
          >
            <img
              src={buildImageUrl(game.cover)}
              alt={game.title}
              className={styles.thumbImage}
            />
            <div className={styles.thumbOverlay}>
              <span className={styles.thumbName}>{game.title}</span>
            </div>
          </button>
        ))}
      </div>

      <div className={styles.indicators}>
        {games.map((_, index) => (
          <button
            key={index}
            className={`${styles.indicator} ${index === current ? styles.active : ''}`}
            onClick={() => goTo(index)}
          />
        ))}
      </div>
    </div>
  );
};

export default DesktopCarousel;
